import classNames from "classnames";
import { t } from "i18next";
import React, { useState } from "react";

import useCustomerInfo from "@repo/shopifyV2/hooks/useCustomerInfo";
import type { ICaaCustomerAddress } from "@repo/shopifyV2/interface/customer-account";
import type { MailingAddress } from "@repo/shopifyV2/types/storefront.types";
import { formatOrderAddress } from "@repo/shopifyV2/utils/address";
import { extractId } from "@repo/shopifyV2/utils/common";

import Button from "@atoms/Button";
import Input from "@atoms/Input";

import Address from "@molecules/Address";

import AddressForm from "@organisms/Address/AddressForm";
import AddressFormIntl from "@organisms/Address/AddressFormIntl";
import AddressLayout from "@organisms/Address/AddressLayout";

import useAddresses from "@hooks/useAddresses";
import useLanguage from "@hooks/useLanguage";

import useCustomer from "@queries/useCustomer";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IProps {
  selectedAddress?: MailingAddress;
  selectedAddressId: string;
  setSelectedAddressId: React.Dispatch<React.SetStateAction<string>>;
  isLoading?: boolean;
}

export default function ShippingAddress({ selectedAddress, selectedAddressId, setSelectedAddressId, isLoading = false }: IProps) {
  const { lang } = useLanguage();

  const { customer, token } = useCustomerStore();
  const { openModal, setIsLoading: setIsModalLoading } = useLayoutStore();

  const { fetchCustomer, deleteAddress } = useCustomer();

  const { addresses, isPartnership } = useCustomerInfo(customer, "thisisneverthat");

  // 파트너십 고객은 배송지가 없을 때만 1개 추가 가능 (이후에는 수정만 가능)
  const canAddAddress = !isPartnership || addresses.length === 0;

  const { defaultAddress } = useAddresses();

  const [selectButton, setSelectButton] = useState<"new" | "change" | "">("");
  const [addressId, setAddressId] = useState(selectedAddressId);
  const [editableAddress, setEditableAddress] = useState<MailingAddress | ICaaCustomerAddress | undefined>();
  const [showForm, setShowForm] = useState(false);
  const [showAddressList, setShowAddressList] = useState<boolean>(false);

  const onCompletedMutation = async (id?: string) => {
    const data = await fetchCustomer({ customerAccessToken: token.accessToken });
    if (!data?.customer) return;

    if (!data?.customer.addresses?.edges?.length || !data?.customer.defaultAddress?.id) {
      setAddressId("");
    } else {
      setAddressId(id ?? data?.customer.defaultAddress.id);
      setSelectedAddressId(id ?? data?.customer.defaultAddress.id);
    }

    // setShowAddressList(false);
    setShowForm(false);
  };

  const handleDeleteAddress = (id: string) => {
    openModal({
      title: "Remove Address",
      content: <p>{t("address_book.delete_address")}</p>,
      confirm: {
        label: "Delete",
        onClick: () => {
          setIsModalLoading(true);
          void deleteAddress({ id }).then(async () => {
            await onCompletedMutation();
            setAddressId(defaultAddress?.id || "");
            setIsModalLoading(false);
            openModal(null);
          });
        },
      },
      cancel: {
        label: "Cancel",
        onClick: () => {
          openModal(null);
        },
      },
    });
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditableAddress(undefined);
  };

  const handleFormCompleted = async (newAddressId?: string) => {
    await onCompletedMutation(newAddressId);

    if (!editableAddress) {
      setShowForm(false);
    } else {
      setShowAddressList(true);
    }

    setEditableAddress(undefined);
  };

  // 배송지 정보
  const shippingAddress = formatOrderAddress({ address: selectedAddress, lang });

  return (
    <section className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between">
        <h2 className="leading-none">Shipping Address</h2>

        <div className="flex gap-2.5">
          {canAddAddress ? (
            <Button
              as="square"
              disabled={isLoading}
              onClick={() => {
                setSelectButton("new");
                setShowForm(true);
                setShowAddressList(false);
                setEditableAddress(undefined);
              }}
              variant="secondary"
            >
              {selectButton === "new" && isLoading ? "Loading..." : t("address_book.create_new_address")}
            </Button>
          ) : null}

          <Button
            as="square"
            disabled={isLoading}
            onClick={() => {
              setSelectButton("change");
              setShowAddressList(!showAddressList);
              setShowForm(showAddressList);
            }}
            variant="secondary"
          >
            {selectButton === "change" && isLoading ? "Loading..." : t("address_book.use_other")}
          </Button>
        </div>
      </div>

      {Object.values(shippingAddress).some((item) => item) ? (
        <Address {...shippingAddress} className={classNames(isLoading && "text-grey")} />
      ) : (
        <span>{t("address_book.empty_address")}</span>
      )}

      {/* Address list */}
      {showAddressList || showForm ? (
        <AddressLayout
          handleCancel={() => {
            setEditableAddress(undefined);

            if (!selectedAddressId && addresses.length) {
              setSelectedAddressId(addresses[0].id);
            }

            if (showAddressList && !showForm) setShowAddressList(false);
            if (!showAddressList && showForm) setShowForm(false);
            if (showAddressList && showForm) {
              setShowForm(false);
              setShowAddressList(true);
            }
          }}
          isEdit={selectButton === "change" && showForm}
        >
          {showForm ? (
            <div className="pc:pt-0 scrollbar-hide col-span-8 flex h-[calc(100dvh-11.5rem)] flex-col gap-5 overflow-y-scroll pt-4">
              <div className="flex flex-col gap-2.5">
                <h2 className="text-title">{editableAddress ? t("address_book.edit_address") : t("address_book.create_new_address")}</h2>
              </div>
              {lang === "ko" && (
                <AddressForm
                  address={editableAddress as MailingAddress | undefined}
                  isDefaultAddress={defaultAddress?.id === editableAddress?.id}
                  onClickCancel={handleCancelForm}
                  onCompletedMutation={handleFormCompleted}
                />
              )}
              {lang === "en" && (
                <AddressFormIntl
                  address={editableAddress as MailingAddress | undefined}
                  className="tnt-gap-x col-span-8 grid grid-cols-6 gap-y-2.5"
                  isDefaultAddress={defaultAddress?.id === editableAddress?.id}
                  isEmptyAddress={!defaultAddress?.id}
                  onClickCancel={handleCancelForm}
                  onCompletedMutation={handleFormCompleted}
                />
              )}
            </div>
          ) : (
            <div className="pc:pt-0 scrollbar-hide col-span-8 flex h-[calc(100dvh-8rem)] flex-col gap-5 overflow-y-scroll pt-4">
              <div className="flex flex-col gap-2.5">
                <h2 className="text-title">{t("address_book.save_list")}</h2>
              </div>

              {addresses.length > 0 ? (
                <ul className="pc:pt-0 flex flex-col gap-5">
                  {addresses.map((node) => {
                    const address = formatOrderAddress({ address: node, lang });

                    return (
                      <li className="border-grey flex flex-col gap-2.5 border-b pb-5" key={node.id}>
                        <Input
                          checked={extractId(addressId) === extractId(node.id)}
                          className="items-start gap-2.5!"
                          id={node.id}
                          label={<Address {...address} />}
                          name="address"
                          onChange={() => {
                            setAddressId(node.id);
                          }}
                          type="radio"
                          value={node.id}
                        />
                        <div className="ml-5.5 flex gap-2.5">
                          <Button
                            as="text"
                            isActive={false}
                            onClick={() => {
                              setShowForm(true);
                              setShowAddressList(true);
                              setEditableAddress(node);
                            }}
                          >
                            {t("edit")}
                          </Button>
                          {!isPartnership && (
                            <Button
                              as="text"
                              isActive={false}
                              onClick={() => {
                                handleDeleteAddress(node.id);
                              }}
                            >
                              {t("delete")}
                            </Button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <span>{t("address_book.empty_address")}</span>
              )}

              <div className="absolute bottom-0 left-0 flex w-full gap-1 bg-white p-4">
                {canAddAddress ? (
                  <Button
                    as="square"
                    className="w-full"
                    disabled={isLoading}
                    onClick={() => {
                      setShowForm(true);
                      setShowAddressList(true);
                      setEditableAddress(undefined);
                    }}
                    variant="secondary"
                  >
                    {t("address_book.create_new_address")}
                  </Button>
                ) : null}
                <Button
                  as="square"
                  className="w-full"
                  disabled={isLoading}
                  onClick={() => {
                    setShowForm(false);
                    setShowAddressList(false);
                    setEditableAddress(undefined);
                    setSelectedAddressId(addressId);
                  }}
                >
                  {t("address_book.select_address")}
                </Button>
              </div>
            </div>
          )}
        </AddressLayout>
      ) : null}
    </section>
  );
}
